import React, {useEffect, useState} from 'react';
import {
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {CloseOutlined, FileDownload, Replay} from "@mui/icons-material";
import {format, parseISO} from "date-fns";
import PageTitle from "./layout/PageTitle";
import TableHeader from "./layout/table/TableHeader";
import NoDataLabel from "./layout/NoDataLabel";
import Loader from "./layout/loading/loader";
import ModalDelete from "./layout/ModalDelete";
import {toastHelper} from "../../utils/toast-helper";
import {
    useGetMyExportsQuery,
    useRegenerateExportMutation,
    useDeleteExportMutation,
    useConsulterExportsMutation,
    downloadExport,
    EXPORT_STATUS,
} from "../../modules/exports/exports.api";

const STATUT_OPTIONS = [
    {value: '', label: 'Tous'},
    {value: EXPORT_STATUS.PENDING, label: 'En attente'},
    {value: EXPORT_STATUS.PROCESSING, label: 'En cours'},
    {value: EXPORT_STATUS.DONE, label: 'Prêt'},
    {value: EXPORT_STATUS.FAILED, label: 'Échoué'},
    {value: EXPORT_STATUS.EXPIRED, label: 'Expiré'},
];

const statutChip = (exportRow) => {
    switch (exportRow.statut) {
        case EXPORT_STATUS.DONE:
            return <Chip label="Prêt" color="success" size="small"/>;
        case EXPORT_STATUS.PENDING:
            return <Chip label="En attente" size="small"/>;
        case EXPORT_STATUS.PROCESSING:
            return <Chip label="En cours" color="info" size="small"/>;
        case EXPORT_STATUS.FAILED:
            return (
                <Tooltip title={exportRow.errorMessage || 'Erreur inconnue'}>
                    <Chip label="Échoué" color="error" size="small"/>
                </Tooltip>
            );
        case EXPORT_STATUS.EXPIRED:
            return <Chip label="Expiré (fichier purgé)" color="warning" size="small"/>;
        default:
            return <Chip label={exportRow.statut} size="small"/>;
    }
};

const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
    return `${Math.max(1, Math.round(bytes / 1024))} Ko`;
};

const formatPeriode = (payload) => {
    if (!payload?.dateDebut || !payload?.dateFin) return '—';
    try {
        return `${format(parseISO(payload.dateDebut), 'dd/MM/yyyy')} → ${format(parseISO(payload.dateFin), 'dd/MM/yyyy')}`;
    } catch (e) {
        return '—';
    }
};

const headCells = [
    {id: 'filename', label: 'Document'},
    {id: 'patrimoineLibelle', label: 'Patrimoine'},
    {id: 'periode', label: 'Période'},
    {id: 'createdAt', label: 'Généré le'},
    {id: 'fileSize', label: 'Taille'},
    {id: 'statut', label: 'Statut'},
];

function MesExportsPage() {
    const [statutFilter, setStatutFilter] = useState('');
    const [patrimoineFilter, setPatrimoineFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const {data: exportsList, isLoading} = useGetMyExportsQuery(
        {statuts: statutFilter ? [statutFilter] : [], patrimoine: patrimoineFilter || undefined},
        {refetchOnMountOrArgChange: true},
    );
    const [regenerateExport] = useRegenerateExportMutation();
    const [deleteExport, {isLoading: isDeleting}] = useDeleteExportMutation();
    const [consulterExports] = useConsulterExportsMutation();

    // Export en attente de confirmation de suppression (null = modale fermée).
    const [exportToDelete, setExportToDelete] = useState(null);

    // À l'ouverture de la page : les exports prêts sont considérés consultés → badge à zéro.
    useEffect(() => {
        consulterExports();
    }, [consulterExports]);

    const handleRegenerate = async (uuid) => {
        try {
            await regenerateExport(uuid).unwrap();
            toastHelper.displaySuccess("Regénération lancée — le document apparaîtra comme prêt une fois généré.");
        } catch (e) {
            if (e?.status === 429 && e?.data?.message) {
                toastHelper.handleError(null, e.data.message);
            } else {
                toastHelper.handleError(e, "Impossible de relancer cet export.");
            }
        }
    };

    const handleDelete = async () => {
        try {
            await deleteExport(exportToDelete.uuid).unwrap();
            toastHelper.displaySuccess("Export supprimé.");
        } catch (e) {
            toastHelper.handleError(e, "Impossible de supprimer cet export.");
        } finally {
            setExportToDelete(null);
        }
    };

    // Pattern DESIGN.md (§3, page liste) : actions par ligne en boutons texte,
    // « Supprimer » rouge + ModalDelete — pas d'icônes seules pour du destructif.
    // Pas de suppression pour pending/processing : le back refuse (409) tant que
    // la génération est en cours — on n'affiche donc pas le bouton.
    const supprimerButton = (exportRow) => (
        <Button color="error" size="small" startIcon={<CloseOutlined/>}
                onClick={() => setExportToDelete(exportRow)}>
            Supprimer
        </Button>
    );

    const actionsCell = (primary, exportRow) => (
        <Grid container spacing={1} justifyContent="center" wrap="nowrap">
            <Grid>{primary}</Grid>
            <Grid>{supprimerButton(exportRow)}</Grid>
        </Grid>
    );

    const renderAction = (exportRow) => {
        switch (exportRow.statut) {
            case EXPORT_STATUS.DONE:
                return actionsCell(
                    <Button color="secondary" size="small" startIcon={<FileDownload/>}
                            onClick={() => downloadExport(exportRow.uuid)}>
                        Télécharger
                    </Button>,
                    exportRow,
                );
            case EXPORT_STATUS.PENDING:
            case EXPORT_STATUS.PROCESSING:
                return <CircularProgress size={20}/>;
            case EXPORT_STATUS.FAILED:
                return actionsCell(
                    <Tooltip title={exportRow.errorMessage || 'Erreur inconnue'}>
                        <Button color="primary" size="small" startIcon={<Replay/>}
                                onClick={() => handleRegenerate(exportRow.uuid)}>
                            Réessayer
                        </Button>
                    </Tooltip>,
                    exportRow,
                );
            case EXPORT_STATUS.EXPIRED:
                return actionsCell(
                    <Button color="primary" size="small" startIcon={<Replay/>}
                            onClick={() => handleRegenerate(exportRow.uuid)}>
                        Regénérer
                    </Button>,
                    exportRow,
                );
            default:
                return null;
        }
    };

    const rows = exportsList || [];
    const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <>
            <PageTitle title={"Mes exports"} icon={<FileDownload/>}/>

            {isLoading && <Loader/>}

            {!isLoading &&
                <>
                    <Grid container spacing={2} className={"mt-2 mb-2"}>
                        <Grid size={3}>
                            <TextField
                                fullWidth
                                label="Filtrer par patrimoine"
                                value={patrimoineFilter}
                                onChange={(e) => {
                                    setPatrimoineFilter(e.target.value);
                                    setPage(0);
                                }}
                            />
                        </Grid>
                        <Grid size={3}>
                            <FormControl fullWidth>
                                <InputLabel id="label-statut-filter">Statut</InputLabel>
                                <Select
                                    labelId="label-statut-filter"
                                    label="Statut"
                                    value={statutFilter}
                                    onChange={(e) => {
                                        setStatutFilter(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    {STATUT_OPTIONS.map((o) => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHeader headCells={headCells}/>
                            <TableBody>
                                {paginatedRows.map((exportRow) => (
                                    <TableRow key={exportRow.uuid}>
                                        <TableCell>{exportRow.filename}</TableCell>
                                        <TableCell>{exportRow.patrimoineLibelle || '—'}</TableCell>
                                        <TableCell>{formatPeriode(exportRow.payload)}</TableCell>
                                        <TableCell>
                                            {exportRow.completedAt
                                                ? format(parseISO(exportRow.completedAt), 'dd/MM/yyyy HH:mm')
                                                : format(parseISO(exportRow.createdAt), 'dd/MM/yyyy HH:mm')}
                                        </TableCell>
                                        <TableCell>{formatFileSize(exportRow.fileSize)}</TableCell>
                                        <TableCell>{statutChip(exportRow)}</TableCell>
                                        <TableCell align="center">{renderAction(exportRow)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {rows.length === 0 &&
                            <NoDataLabel label={"Aucun export pour le moment — lancez une synthèse PDF depuis l'écran Synthèse."}/>
                        }
                        <TablePagination
                            component="div"
                            count={rows.length}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                            labelRowsPerPage="Lignes par page"
                        />
                    </TableContainer>

                    <ModalDelete
                        open={exportToDelete !== null}
                        onClose={() => setExportToDelete(null)}
                        onDelete={handleDelete}
                        loading={isDeleting}
                        label={"Voulez-vous vraiment supprimer l'export"}
                        objectToDelete={exportToDelete?.filename}
                    />
                </>
            }
        </>
    );
}

export default MesExportsPage;
